import { useContext, useEffect, useState } from "react";
import { WorkspaceCtx } from "@/components/app/navConfig";
import { CheckCircle2, AlertTriangle, AlertOctagon, Loader2, Wifi, WifiOff, CloudUpload, Info } from "lucide-react";
import { outbox, errRef, errMsg } from "@/lib/api";
import { fmtValue } from "@/lib/fmt";
import UpgradeGate from "@/components/app/UpgradeGate";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const STATUS = {
  on_track: { label: "On Track", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  watch: { label: "Watch", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle },
  action: { label: "Action Needed", cls: "bg-rose-50 text-rose-800 border-rose-200 font-semibold", Icon: AlertOctagon },
  missing: { label: "Not recorded", cls: "bg-stone-50 text-stone-600 border-stone-200", Icon: AlertTriangle },
};

export const StatusBadge = ({ status, testid }) => {
  const s = STATUS[status];
  if (!s) return null;
  return (
    <span data-testid={testid || `status-${status}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${s.cls}`}>
      <s.Icon className="h-3 w-3" aria-hidden /> {s.label}
    </span>
  );
};

export const Hint = ({ text }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="text-stone-400 hover:text-stone-700" aria-label="More info"><Info className="h-3.5 w-3.5" /></button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const KpiCard = ({ k }) => (
  <div data-testid={`kpi-${k.key}`} className="card-surface p-4 md:p-5 fade-up flex flex-col gap-2">
    <div className="flex items-start justify-between gap-x-2 gap-y-1 flex-wrap">
      <span className="text-xs text-stone-500 font-medium flex items-center gap-1">{k.label}{k.hint && <Hint text={k.hint} />}</span>
      {k.status && <StatusBadge status={k.status} testid={`kpi-${k.key}-status`} />}
    </div>
    <div className="text-2xl md:text-3xl font-semibold num tracking-tight text-stone-900">{fmtValue(k.value, k.fmt)}</div>
    <div className="text-xs text-stone-500 min-h-[16px]">
      {k.target != null && <>Target {fmtValue(k.target, k.fmt)}</>}
      {k.dollars != null && <> · {fmtValue(k.dollars, "money")}</>}
      {k.sub && <>{k.sub}</>}
    </div>
    {k.action && <div className="text-xs text-[#9E2A2B] font-medium">→ {k.action}</div>}
  </div>
);

export const PageHeader = ({ eyebrow, title, subtitle, children }) => {
  const ws = useContext(WorkspaceCtx);
  return (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6 md:mb-8">
    <div>
      {eyebrow && !ws && <div className="label-eyebrow mb-2">{eyebrow}</div>}
      <h1 data-testid="page-title" className="text-3xl md:text-4xl font-display font-semibold text-stone-900">{title}</h1>
      {subtitle && <p className="text-sm md:text-base text-stone-600 mt-2 max-w-2xl">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2 no-print">{children}</div>}
  </div>
  );
};

export const EmptyState = ({ icon: Icon, title, text, action, testid = "empty-state" }) => (
  <div data-testid={testid} className="card-surface border-dashed p-8 md:p-12 text-center flex flex-col items-center gap-3">
    {Icon && <div className="h-12 w-12 rounded-full bg-[#F3EFE6] flex items-center justify-center"><Icon className="h-5 w-5 text-stone-700" /></div>}
    <div className="font-display text-xl text-stone-900">{title}</div>
    {text && <p className="text-sm text-stone-600 max-w-md">{text}</p>}
    {action}
  </div>
);

export const Loading = ({ label = "Loading…" }) => (
  <div data-testid="loading" className="flex items-center gap-2 text-stone-500 text-sm py-10 justify-center">
    <Loader2 className="h-4 w-4 animate-spin" /> {label}
  </div>
);

export const ErrorBox = ({ error, onRetry }) => {
  const d = error?.response?.data?.detail;
  const ref = errRef(error);
  if (typeof d === "string" && (d.startsWith("upgrade_required") || d.startsWith("permission_and_plan_required"))) return <UpgradeGate detail={d} />;
  if (error?.response?.status === 401) return (
    <div data-testid="session-ended-error" className="card-surface p-6 flex items-start gap-3" role="alert">
      <AlertTriangle className="h-5 w-5 text-stone-500 mt-0.5" />
      <div><div className="font-medium">Your session ended</div>
        <a href="/login" className="text-sm underline" data-testid="session-ended-signin">Sign in again</a></div>
    </div>
  );
  if (error?.response?.status === 403) return (
    <div data-testid="no-access-error" className="card-surface p-6 flex items-start gap-3">
      <AlertTriangle className="h-5 w-5 text-stone-500 mt-0.5" />
      <div>
        <div className="font-medium">You don't have access to this</div>
        <div className="text-sm text-stone-600">{typeof d === "string" && d.length < 200 ? d : "Your account doesn't have permission for this. Ask the owner or GM if you need it."}</div>
      </div>
    </div>
  );
  return (
    <div data-testid="error-box" className="card-surface p-6 flex items-start gap-3" role="alert">
      <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
      <div>
        <div className="font-medium">Couldn't load this</div>
        <div className="text-sm text-stone-600" data-testid="error-box-message">{d != null ? errMsg(error) : error?.response ? "Something went wrong on our side." : "Check your connection and try again."}</div>
        {onRetry && <button type="button" onClick={onRetry} className="mt-2 text-sm font-semibold underline" data-testid="error-retry">Try again</button>}
        {ref && <div className="text-[11px] text-stone-400 font-mono mt-1">Ref {ref}</div>}
      </div>
    </div>
  );
};

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(outbox.list().length);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const unsub = outbox.subscribe(setQueued);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); unsub(); };
  }, []);
  return { online, queued };
}

export const NetStatus = () => {
  const { online, queued } = useOnline();
  if (online && !queued) return <span data-testid="net-status" className="hidden md:inline-flex items-center gap-1 text-xs text-stone-500"><Wifi className="h-3.5 w-3.5" /> Saved</span>;
  return (
    <span data-testid="net-status" className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium">
      {online ? <CloudUpload className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
      {online ? `Syncing ${queued}…` : queued ? `Waiting for connection · ${queued} saved on device` : "Offline"}
    </span>
  );
};

export const Section = ({ title, action, children, testid }) => (
  <section data-testid={testid} className="mb-8 min-w-0">
    <div className="flex items-center justify-between mb-3">
      <h2 className="font-display text-xl md:text-2xl text-stone-900">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

export const Pill = ({ children, tone = "stone" }) => {
  const tones = { stone: "bg-stone-100 text-stone-700", red: "bg-rose-50 text-rose-800", amber: "bg-amber-50 text-amber-800", green: "bg-emerald-50 text-emerald-800", dark: "bg-stone-900 text-white" };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>{children}</span>;
};

export const priorityTone = (p) => ({ Critical: "red", High: "amber", Normal: "stone", Low: "stone" }[p] || "stone");
